/**
 * PEP free-gift rules engine.
 * Keeps one property-identified gift line per qualifying rule and republishes
 * the theme's cart:update event with authoritative paid-subtotal data.
 */
(function () {
  var config = window.PEP_GIFTS || {};
  var SOURCE = 'pep-free-gift';
  var nativeFetch = window.fetch.bind(window);
  var knownProps = config.knownProps || ['_free_gift', '_free_gift_2'];
  var rules = (config.rules || []).map(function (rule) {
    return {
      prop: rule.prop,
      variantId: Number(rule.variantId || 0),
      handle: String(rule.handle || ''),
      title: cleanTitle(rule.title || 'Free gift'),
      minSubtotal: Math.max(0, Number(rule.minSubtotal || 0)),
      enabled: rule.enabled !== false,
      available: rule.available !== false,
      legacy: rule.legacy === true,
      showProgress: rule.showProgress === true,
    };
  });
  var busy = false;
  var pending = false;
  var pendingContext = null;
  var activePromise = null;
  var debounceTimer = null;
  var verifyTimerOne = null;
  var verifyTimerTwo = null;
  var legacyMigrationOpen = true;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cleanTitle(title) {
    return String(title || '').replace(/\s*\(Free Item\)\s*$/i, '').trim();
  }

  function hasGiftProperty(item) {
    var properties = item && item.properties ? item.properties : {};
    for (var i = 0; i < knownProps.length; i += 1) {
      if (Object.prototype.hasOwnProperty.call(properties, knownProps[i])) return knownProps[i];
    }
    return '';
  }

  function paidState(cart) {
    var paidQty = 0;
    var paidSubtotal = 0;
    (cart.items || []).forEach(function (item) {
      if (hasGiftProperty(item)) return;
      paidQty += Number(item.quantity || 0);
      paidSubtotal += Number(item.final_line_price || 0);
    });
    return { paidQty: paidQty, paidSubtotal: paidSubtotal };
  }

  function activeRules() {
    return rules.filter(function (rule) {
      return rule.enabled && rule.prop && rule.variantId > 0;
    });
  }

  function cartJs() {
    return nativeFetch(root() + 'cart.js', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart read failed: ' + response.status);
      return response.json();
    });
  }

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (element) {
      var id = element.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function currentSections() {
    var sections = {};
    sectionIds().forEach(function (id) {
      var section = document.getElementById('shopify-section-' + id);
      if (section) sections[id] = section.outerHTML;
    });
    return sections;
  }

  function fetchSections() {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve({});
    var url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    return nativeFetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : {};
      })
      .catch(function () {
        return {};
      });
  }

  function post(path, body) {
    var ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return nativeFetch(root() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.status >= 400 && response.status < 500) {
        var error = new Error('Cart mutation rejected: ' + response.status);
        error.clientError = true;
        throw error;
      }
      if (!response.ok) throw new Error('Cart mutation failed: ' + response.status);
      return response.json();
    });
  }

  function correction(cart) {
    var active = activeRules();
    var byProp = {};
    var state = paidState(cart);
    active.forEach(function (rule) { byProp[rule.prop] = rule; });

    for (var i = 0; i < (cart.items || []).length; i += 1) {
      var item = cart.items[i];
      var prop = hasGiftProperty(item);
      if (!prop) continue;
      if (!byProp[prop] || Number(item.variant_id) !== byProp[prop].variantId) {
        return { type: 'change', body: { id: item.key, quantity: 0 } };
      }
    }

    for (var r = 0; r < active.length; r += 1) {
      var rule = active[r];
      var lines = (cart.items || []).filter(function (line) {
        return hasGiftProperty(line) === rule.prop && Number(line.variant_id) === rule.variantId;
      });
      var qualifies = state.paidQty > 0 && state.paidSubtotal >= rule.minSubtotal;
      if (!qualifies && lines.length) return { type: 'change', body: { id: lines[0].key, quantity: 0 } };
      if (!qualifies) continue;
      if (lines.length > 1) return { type: 'change', body: { id: lines[1].key, quantity: 0 } };
      if (lines.length === 1 && Number(lines[0].quantity) !== 1) {
        return { type: 'change', body: { id: lines[0].key, quantity: 1 } };
      }
      if (!lines.length && rule.legacy && legacyMigrationOpen) {
        var legacy = (cart.items || []).find(function (line) {
          return !hasGiftProperty(line) && Number(line.variant_id) === rule.variantId && Number(line.final_line_price) === 0;
        });
        legacyMigrationOpen = false;
        if (legacy) return { type: 'change', body: { id: legacy.key, quantity: 0 }, legacy: true };
      }
      if (!lines.length && rule.available) {
        return {
          type: 'add',
          rule: rule,
          body: { items: [{ id: rule.variantId, quantity: 1, properties: propertyObject(rule.prop) }] },
        };
      }
    }
    legacyMigrationOpen = false;
    return null;
  }

  function propertyObject(prop) {
    var properties = {};
    properties[prop] = '1';
    return properties;
  }

  function ruleStates(cart) {
    var state = paidState(cart);
    return activeRules().map(function (rule) {
      return {
        prop: rule.prop,
        title: rule.title,
        minSubtotal: rule.minSubtotal,
        showProgress: rule.showProgress,
        qualifies: state.paidQty > 0 && state.paidSubtotal >= rule.minSubtotal,
      };
    });
  }

  function broadcast(cart, sections) {
    var state = paidState(cart);
    var states = ruleStates(cart);
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: SOURCE,
        data: {
          itemCount: Number(cart.item_count || 0),
          source: SOURCE,
          sections: sections || {},
          _pepGift: {
            source: true,
            paidQty: state.paidQty,
            paidSubtotal: state.paidSubtotal,
            rules: states,
          },
        },
      },
    }));
    updateProgress(state.paidSubtotal, states);
  }

  function cleanupGiftRows(cart) {
    var keys = {};
    (cart.items || []).forEach(function (item) {
      if (hasGiftProperty(item)) keys[item.key] = true;
    });
    document.querySelectorAll('[data-pep-gift-prop][data-key]').forEach(function (row) {
      if (!keys[row.getAttribute('data-key')]) row.remove();
    });
  }

  function sectionsForPass(context, lastSections, changed) {
    if (lastSections && Object.keys(lastSections).length) return Promise.resolve(lastSections);
    if (context.sections && Object.keys(context.sections).length) return Promise.resolve(context.sections);
    if (!changed && context.reason === 'initial') return Promise.resolve(currentSections());
    if (!changed) return fetchSections();
    return Promise.resolve({});
  }

  function runPass(cart, pass, context, addedNames, lastSections) {
    var action = correction(cart);
    if (!action || pass >= 8) {
      return sectionsForPass(context, lastSections, false).then(function (sections) {
        broadcast(cart, sections);
        cleanupGiftRows(cart);
        return { cart: cart, addedNames: addedNames };
      });
    }
    var path = action.type === 'add' ? 'cart/add.js' : 'cart/change.js';
    return post(path, action.body)
      .then(function (response) {
        if (action.type === 'add' && action.rule) addedNames.push(action.rule.title);
        return cartJs().then(function (nextCart) {
          var sections = response.sections || {};
          broadcast(nextCart, sections);
          cleanupGiftRows(nextCart);
          return runPass(nextCart, pass + 1, context, addedNames, sections);
        });
      })
      .catch(function (error) {
        if (!error.clientError) throw error;
        broadcast(cart, lastSections || context.sections || currentSections());
        cleanupGiftRows(cart);
        return { cart: cart, addedNames: addedNames };
      });
  }

  function reconcile(context) {
    context = context || { reason: 'manual', sections: {} };
    if (busy) {
      pending = true;
      pendingContext = context;
      return activePromise || Promise.resolve();
    }
    busy = true;
    activePromise = cartJs()
      .then(function (cart) { return runPass(cart, 0, context, [], context.sections || {}); })
      .then(function (result) {
        announceAdded(result.addedNames);
        return result.cart;
      })
      .catch(function () {})
      .finally(function () {
        busy = false;
        activePromise = null;
        if (pending) {
          var nextContext = pendingContext || { reason: 'pending', sections: {} };
          pending = false;
          pendingContext = null;
          reconcile(nextContext);
        }
      });
    return activePromise;
  }

  function resetNotification() {
    document.dispatchEvent(new CustomEvent('pep:gifts-notification-reset'));
  }

  function announceAdded(names) {
    var unique = [];
    names.forEach(function (name) { if (name && unique.indexOf(name) === -1) unique.push(name); });
    if (!unique.length) return;
    document.dispatchEvent(new CustomEvent('pep:gifts-added', {
      detail: { giftNames: unique, giftTitle: unique[0] },
    }));
  }

  function formatMoney(cents) {
    var format = config.moneyFormat || '${{amount}}';
    var value = Number(cents || 0);
    function withDelimiters(number, precision, thousands, decimal) {
      var fixed = (number / 100).toFixed(precision).split('.');
      fixed[0] = fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return fixed[0] + (precision ? decimal + fixed[1] : '');
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(value);
    var token = match[1];
    var comma = token.indexOf('comma_separator') !== -1;
    var rendered = token.indexOf('no_decimals') !== -1
      ? withDelimiters(value, 0, comma ? '.' : ',', comma ? ',' : '.')
      : withDelimiters(value, 2, comma ? '.' : ',', comma ? ',' : '.');
    return format.replace(match[0], rendered);
  }

  function updateProgress(paidSubtotal, states) {
    document.querySelectorAll('[data-pep-gift-progress]').forEach(function (element) {
      var prop = element.getAttribute('data-gift-prop');
      var state = states.find(function (candidate) { return candidate.prop === prop; });
      if (!state || !state.showProgress || state.minSubtotal <= 0) {
        element.hidden = true;
        return;
      }
      element.hidden = false;
      var unlocked = state.qualifies;
      var previous = element.getAttribute('data-unlocked') === 'true';
      var crossing = previous !== unlocked;
      if (!crossing) element.classList.remove('is-transition-ready');
      var percent = unlocked ? 100 : Math.min(99, Math.floor((paidSubtotal / state.minSubtotal) * 100));
      var remaining = Math.max(0, state.minSubtotal - paidSubtotal);
      element.style.setProperty('--pep-gift-progress', percent + '%');
      element.classList.toggle('is-unlocked', unlocked);
      element.setAttribute('data-unlocked', String(unlocked));
      element.setAttribute('aria-valuenow', String(Math.min(paidSubtotal, state.minSubtotal)));
      var locked = element.querySelector('[data-pep-progress-locked]');
      var unlockedText = element.querySelector('[data-pep-progress-unlocked]');
      if (locked) locked.hidden = unlocked;
      if (unlockedText) unlockedText.hidden = !unlocked;
      var amount = element.querySelector('[data-pep-remaining]');
      if (amount) {
        amount.setAttribute('data-cents', String(remaining));
        amount.textContent = formatMoney(remaining);
      }
      if (!crossing) requestAnimationFrame(function () { element.classList.add('is-transition-ready'); });
    });
  }

  function enableProgressTransitions() {
    requestAnimationFrame(function () {
      document.querySelectorAll('[data-pep-gift-progress]').forEach(function (element) {
        element.setAttribute('data-unlocked', String(element.classList.contains('is-unlocked')));
        element.classList.add('is-transition-ready');
      });
    });
  }

  var toastEl = null;
  var toastTimer = null;
  var toastNames = [];
  function buildToast() {
    var element = document.createElement('div');
    element.className = 'pep-gift-toast';
    element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite');
    element.innerHTML = '<span class="pep-gift-toast__icon" aria-hidden="true">🎁</span><span class="pep-gift-toast__msg"></span><button type="button" class="pep-gift-toast__close" aria-label="Dismiss">×</button>';
    element.querySelector('.pep-gift-toast__close').addEventListener('click', hideToast);
    document.body.appendChild(element);
    return element;
  }
  function renderToast(names) {
    if (config.showPopup === false || !document.body) return;
    names.forEach(function (name) { if (toastNames.indexOf(name) === -1) toastNames.push(name); });
    if (!toastNames.length) return;
    if (!toastEl) toastEl = buildToast();
    toastEl.querySelector('.pep-gift-toast__msg').textContent = toastNames.length === 1
      ? (config.message || 'Free gift added to your order!') + ' ' + toastNames[0] + '.'
      : toastNames.length + ' free gifts added: ' + toastNames.join(', ') + '.';
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4500);
  }
  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = null;
  }
  document.addEventListener('pep:gifts-added', function (event) {
    renderToast((event.detail && event.detail.giftNames) || []);
  });
  document.addEventListener('pep:gifts-notification-reset', function () { toastNames = []; });

  function isCartMutation(url, method) {
    return String(method || 'GET').toUpperCase() !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/.test(String(url || ''));
  }
  function schedule(reason, sections) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { reconcile({ reason: reason, sections: sections || {} }); }, 150);
    if (verifyTimerOne) clearTimeout(verifyTimerOne);
    if (verifyTimerTwo) clearTimeout(verifyTimerTwo);
    verifyTimerOne = setTimeout(function () { reconcile({ reason: 'verify', sections: {} }); }, 1200);
    verifyTimerTwo = setTimeout(function () { reconcile({ reason: 'verify', sections: {} }); }, 3000);
  }
  function observeMutation(url, method) {
    if (!isCartMutation(url, method)) return;
    if (/\/cart\/add/.test(String(url))) resetNotification();
    schedule('mutation', {});
  }
  function installFetchHook() {
    var current = window.fetch;
    if (!current || current._pepGiftHook) return;
    function hookedFetch(input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var method = (init && init.method) || (input && input.method) || 'GET';
      return current.apply(this, arguments).then(function (response) {
        if (response && response.status >= 200 && response.status < 300) observeMutation(url, method);
        return response;
      });
    }
    hookedFetch._pepGiftHook = true;
    window.fetch = hookedFetch;
  }
  function installXhrHook() {
    if (!window.XMLHttpRequest || window.XMLHttpRequest.prototype._pepGiftHook) return;
    var proto = window.XMLHttpRequest.prototype;
    var nativeOpen = proto.open;
    var nativeSend = proto.send;
    proto.open = function (method, url) {
      this._pepGiftMethod = method;
      this._pepGiftUrl = url;
      return nativeOpen.apply(this, arguments);
    };
    proto.send = function () {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) observeMutation(this._pepGiftUrl, this._pepGiftMethod);
      }, { once: true });
      return nativeSend.apply(this, arguments);
    };
    proto._pepGiftHook = true;
  }

  [0, 500, 1500, 3000, 6000].forEach(function (delay) { setTimeout(installFetchHook, delay); });
  installXhrHook();
  document.addEventListener('submit', function (event) {
    var form = event.target;
    var action = form && form.getAttribute ? form.getAttribute('action') : '';
    if (action && /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(action)) {
      resetNotification();
      schedule('form', {});
    }
  }, true);
  document.addEventListener('cart:update', function (event) {
    var data = event.detail && event.detail.data;
    if (data && data._pepGift && data._pepGift.source) return;
    if (data && data.source === 'product-form-component') resetNotification();
    schedule('theme-event', (data && data.sections) || {});
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule('visibility', {});
  });
  window.addEventListener('pageshow', function () { schedule('pageshow', {}); });

  window.PEP_GIFT_ENGINE = { paidState: paidState, reconcile: reconcile };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enableProgressTransitions();
      reconcile({ reason: 'initial', sections: {} });
    });
  } else {
    enableProgressTransitions();
    reconcile({ reason: 'initial', sections: {} });
  }
})();
